import { Injectable, OnDestroy } from '@angular/core';

import {
    BehaviorSubject,
    combineLatest,
    distinctUntilChanged,
    filter,
    map,
    merge,
    Observable,
    Subject,
    Subscription,
    switchMap,
    take,
    tap
} from 'rxjs';
import * as uuid from 'uuid';

import { AccountManagementService } from './account-management.service';
import { QuotaInformation } from '../../../api/mediaSubscriptions/models/quota-information.api.model';
import { userPermissionsEnum } from '../../../Core/Users/enums/user-permissions.enum';
import { MonitoringQuotaTypesEnum } from '@api/mentions/enum/monitoring-quota-types.enum';
import { uniqueId } from 'underscore';

@Injectable()
export class QuotaPreferenceService implements OnDestroy {
    private subscriptions: Subscription[] = [];
    private setRemainingQuota$ = new Subject<number>();
    private monitoringSubscriptionsEnabled$ = new Subject<boolean>();
    private shouldShowQuotaBanner$ = new BehaviorSubject<boolean>(false);

    disableInputs$ = this.accountManagementService.getReadOnly();
    quota$: Observable<QuotaInformation>;
    hasQuota$: Observable<boolean>;
    remainingQuota$: Observable<number>;

    showQuotaBanner$ = this.shouldShowQuotaBanner$.asObservable();

    constructor(
        private accountManagementService: AccountManagementService
    ) {
    }

    ngOnDestroy(): void {
        this.subscriptions?.forEach(sub => sub.unsubscribe());
    }

    setQuotaType(type: MonitoringQuotaTypesEnum): void {
        if (type === MonitoringQuotaTypesEnum.MEDIA_SUBSCRIPTION) {
            this.quota$ = this.accountManagementService.getMediaSubscriptionQuotaGroup().pipe(
                filter(x => !!x),
            );
        } else {
            this.quota$ = this.accountManagementService.getSocialListeningQuotaGroup().pipe(
                filter(x => !!x),
            );
        }
        this.hasQuota$ = this.quota$.pipe(
            map(quota => quota?.enabled),
            distinctUntilChanged()
        );
        this.remainingQuota$ = merge(
            this.quota$.pipe(
                map(quota => {
                    if (!quota.enabled) {
                        return 0;
                    }
                    return this.calculateRemainingQuota(quota);
                })
            ),
            this.setRemainingQuota$.asObservable()
        );
    }

    public setRemainingQuota(quota: QuotaInformation) {
        this.setRemainingQuota$.next(this.calculateRemainingQuota(quota));
    }

    public setMonitoringSubscriptionsEnabled(enabled: boolean) {
        this.accountManagementService.toggleMonitoringQuotaGroup(enabled, MonitoringQuotaTypesEnum.MEDIA_SUBSCRIPTION);
    }

    public setShouldShowQuotaBanner(showBanner: boolean) {
        this.shouldShowQuotaBanner$.next(showBanner);
    }

    private calculateRemainingQuota(quota: QuotaInformation): number {
        return (+quota.maxCount + +quota.additionalCount - +quota.currentCount);
    }
}
